import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import Button from 'modules/_shared/components/Buttons';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';

import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import { TradingOfferType } from 'types/TradingOffer';
import { useTradingOffersList } from '../contexts/TradingOffersListContext';
import {
  TradingOffersSummaryCard,
  TradingOperationsCard,
  TradingOffersSection,
} from '../components';

import '../styles/Trading.scss';

const BUTTON_VARIANT = false;

type TradingOffersListProps = {
  tradingOfferType?: TradingOfferType;
};

const TradingOffersList: FC<TradingOffersListProps> = ({
  tradingOfferType,
}) => {
  const { t } = useTranslate();
  const { data, actions } = useTradingOffersList();
  const location = useLocation();
  const partnerQuery = new URLSearchParams(location.search).get('partnerId');
  const pq = partnerQuery ? `?partnerId=${partnerQuery}` : '';

  useGetMenu(menuTypes.NULL);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: data.societyName, to: `/sociedad/${data.societyId}` },
      {
        label: t('EquityMarket'),
        to: `/mercado-de-acciones/${data.societyId}${pq}`,
      },
      { label: t('TradingOffersList'), isActive: true },
    ],
    [t, data.societyName, data.societyId, pq]
  );

  const pageActions = [
    <Button key="sell-offer" onClick={actions.handleCreateSellOffer}>
      <span>{t('MakeOffer')}</span>
      <em className="icon ni ni-arrow-long-right ml-1" />
    </Button>,
    <Button
      key="buy-sell"
      variant="light"
      onClick={actions.handleCreateBuyOffer}
      disabled
    >
      <span>{t('BuySell')}</span>
      <em className="icon ni ni-arrow-long-right ml-1 d-none d-sm-inline-block" />
    </Button>,
  ];

  const showSellTradingOffers =
    !tradingOfferType || tradingOfferType === 'SELL';

  const showBuyTradingOffers = !tradingOfferType || tradingOfferType === 'BUY';

  return (
    <Page id="trading-offers-list-page" className="pb-5">
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <PageListHeader
        subTitle={t('Offers')}
        description={t('TradingOffersDescription')}
        actions={pageActions}
      />

      <div className="nk-block">
        <div className="row g-gs">
          <div className="col-md-6 col-lg-4">
            <TradingOffersSummaryCard
              type={TradingOfferType.SELL}
              summary={data.sellOffersSummary}
              societyId={data.societyId}
              isButtonVariant={BUTTON_VARIANT}
            />
          </div>

          <div className="col-md-6 col-lg-4">
            <TradingOffersSummaryCard
              type={TradingOfferType.BUY}
              summary={data.buyOffersSummary}
              societyId={data.societyId}
              isButtonVariant={BUTTON_VARIANT}
            />
          </div>

          <div className="col-md-6 col-lg-4">
            <TradingOperationsCard
              operations={data.partnerOperations}
              activeCount={data.activeOperationsCount}
              totalCount={data.totalOperationsCount}
              societyId={data.societyId}
              isButtonVariant={BUTTON_VARIANT}
            />
          </div>
        </div>
      </div>

      {showSellTradingOffers && (
        <TradingOffersSection
          type={TradingOfferType.SELL}
          offers={data.sellOffers}
          sortBy={data.sellOffersSortBy}
          onSortChange={actions.handleSellOffersSortChange}
          onViewDetail={actions.handleViewOfferDetail}
          societyId={data.societyId}
        />
      )}

      {showBuyTradingOffers && (
        <TradingOffersSection
          type={TradingOfferType.BUY}
          offers={data.buyOffers}
          sortBy={data.buyOffersSortBy}
          onSortChange={actions.handleBuyOffersSortChange}
          onViewDetail={actions.handleViewOfferDetail}
          societyId={data.societyId}
        />
      )}

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default TradingOffersList;
